/**
 * Grid rendering for the VTT canvas.
 *
 * The grid is drawn in *screen space* — we compute the on-screen positions
 * of the nearest grid lines and stroke them at integer pixel offsets. This
 * keeps the lines crisp at any zoom, which is much harder to achieve if we
 * draw the grid in map space and let the canvas transform scale it.
 *
 * Step sizing: the base grid cell is 64 map units (the same cell size the
 * tokens will snap to later). When the user zooms way out we'd end up with
 * a grey wash of lines 2px apart; when zoomed way in a single cell would
 * fill the screen. `effectiveStep` picks a power-of-two multiple of the
 * base step so on-screen spacing stays between 24px and 96px regardless of
 * zoom level.
 */

import type { Viewport } from "./viewport";

const BASE_STEP = 64;
const MIN_ON_SCREEN = 24;
const MAX_ON_SCREEN = 96;

export interface GridStyle {
  color: string;
  width: number;
}

const DEFAULT_STYLE: GridStyle = {
  color: "rgba(255, 255, 255, 0.06)",
  width: 1,
};

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  canvasWidth: number,
  canvasHeight: number,
  style: GridStyle = DEFAULT_STYLE,
): void {
  // Pick a grid step whose on-screen size lands in the comfortable band.
  let stepMap = BASE_STEP;
  while (stepMap * vp.zoom < MIN_ON_SCREEN) stepMap *= 2;
  while (stepMap * vp.zoom > MAX_ON_SCREEN) stepMap /= 2;

  const stepScreen = stepMap * vp.zoom;

  // Map-space coord at the screen origin, snapped to the nearest grid line.
  const firstMapX = Math.floor(vp.panX / stepMap) * stepMap;
  const firstMapY = Math.floor(vp.panY / stepMap) * stepMap;

  // Screen x of that first vertical line. Will be ≤ 0.
  const firstScreenX = (firstMapX - vp.panX) * vp.zoom;
  const firstScreenY = (firstMapY - vp.panY) * vp.zoom;

  ctx.save();
  ctx.strokeStyle = style.color;
  ctx.lineWidth = style.width;
  ctx.beginPath();

  for (let x = firstScreenX; x <= canvasWidth; x += stepScreen) {
    const px = Math.round(x) + 0.5; // +0.5 = crisp 1px line
    ctx.moveTo(px, 0);
    ctx.lineTo(px, canvasHeight);
  }
  for (let y = firstScreenY; y <= canvasHeight; y += stepScreen) {
    const py = Math.round(y) + 0.5;
    ctx.moveTo(0, py);
    ctx.lineTo(canvasWidth, py);
  }

  ctx.stroke();
  ctx.restore();
}
